import React, { useState, useEffect } from "react";
import {
  Modal,
  List,
  Input,
  Button,
  Tag,
  Space,
  Typography,
  Spin,
  message,
  notification,
  Empty,
  Divider,
} from "antd";
import { SendOutlined, CheckCircleOutlined, CopyOutlined } from "@ant-design/icons";
import axios from "axios";

import { copyText } from "utils/clipboard";

notification.config({ placement: "topRight" });

const { Text } = Typography;
const { TextArea } = Input;

const SendTemplateModal = ({ open, onClose, businessNumberId, contactWaId, onSuccess }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [waNumbers, setWaNumbers] = useState(contactWaId || "");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (open && businessNumberId) {
      fetchTemplates();
      setSelectedTemplate(null);
      setWaNumbers(contactWaId || "");
      setSearchTerm("");
    }
  }, [open, businessNumberId]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `/whatsapp_api/whatsapp-templates/?business_number=${businessNumberId}`
      );
      if (response.status === 200) {
        const data = response.data.results || response.data;
        // Only show approved templates
        const approved = Array.isArray(data)
          ? data.filter((t) => t.status === "APPROVED")
          : [];
        setTemplates(approved);
      }
    } catch (err) {
      console.error("Error loading templates:", err);
      message.error("Failed to load templates");
    }
    setLoading(false);
  };

  const handleSend = async () => {
    if (!selectedTemplate) {
      message.warning("Please select a template");
      return;
    }

    const numbers = contactWaId
      ? [contactWaId]
      : waNumbers
          .split(",")
          .map((n) => n.trim())
          .filter(Boolean);

    if (numbers.length === 0) {
      message.warning("Please enter at least one WhatsApp number");
      return;
    }

    setSending(true);
    try {
      const response = await axios.post("/whatsapp_api/send-template-message/", {
        business_number_id: businessNumberId,
        wa_numbers: numbers,
        template_id: selectedTemplate.id,
      });
      if (response.status === 200 || response.status === 201) {
        notification.success({
          message: "Template Sent",
          description: `Template message sent to ${numbers.length} contact(s).`,
        });
        if (onSuccess) onSuccess();
        onClose();
      }
    } catch (err) {
      console.error("Error sending template:", err);
      const errorMsg =
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        "Failed to send template message";
      notification.error({
        message: "Send Failed",
        description: errorMsg,
      });
    }
    setSending(false);
  };

  /**
   * The selected template as pasteable text. A template can only be delivered
   * from here while the number is one this CRM holds credentials for, so being
   * able to take the message to another phone matters.
   */
  const handleCopy = async () => {
    if (!selectedTemplate) {
      message.warning("Please select a template");
      return;
    }
    const { header, body, footer } = getTemplatePreview(selectedTemplate);
    const text = [header?.text, body?.text, footer?.text].filter(Boolean).join("\n\n");
    const copied = await copyText(text);
    message[copied ? "success" : "error"](
      copied ? "Message copied" : "Could not copy the message"
    );
  };

  const getTemplatePreview = (template) => {
    const components = template?.components || [];
    const header = components.find((c) => c.type === "HEADER");
    const body = components.find((c) => c.type === "BODY");
    const footer = components.find((c) => c.type === "FOOTER");
    return { header, body, footer };
  };

  const filteredTemplates = templates.filter((t) =>
    t.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Modal
      title="Send Template Message"
      open={open}
      onCancel={onClose}
      width={680}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="copy"
          icon={<CopyOutlined />}
          onClick={handleCopy}
          disabled={!selectedTemplate}
        >
          Copy Message
        </Button>,
        <Button
          key="send"
          type="primary"
          icon={<SendOutlined />}
          onClick={handleSend}
          loading={sending}
          disabled={!selectedTemplate}
          style={{ backgroundColor: "#25D366", borderColor: "#25D366" }}
        >
          Send Template
        </Button>,
      ]}
    >
      {!contactWaId && (
        <div style={{ marginBottom: 16 }}>
          <Text strong>Recipient Numbers (comma-separated):</Text>
          <TextArea
            value={waNumbers}
            onChange={(e) => setWaNumbers(e.target.value)}
            placeholder="e.g. 919876543210, 918765432109"
            rows={2}
            style={{ marginTop: 4 }}
          />
        </div>
      )}

      <Input.Search
        placeholder="Search templates..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        style={{ marginBottom: 12 }}
        allowClear
      />

      {loading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
        </div>
      ) : filteredTemplates.length === 0 ? (
        <Empty description="No approved templates found" />
      ) : (
        <div style={{ maxHeight: 400, overflowY: "auto" }}>
          <List
            dataSource={filteredTemplates}
            renderItem={(item) => {
              const isSelected = selectedTemplate?.id === item.id;
              const preview = getTemplatePreview(item);
              return (
                <List.Item
                  onClick={() => setSelectedTemplate(item)}
                  style={{
                    cursor: "pointer",
                    backgroundColor: isSelected ? "#f0fff4" : "#fff",
                    border: isSelected ? "1px solid #25D366" : "1px solid #f0f0f0",
                    borderRadius: 8,
                    marginBottom: 8,
                    padding: "12px 16px",
                  }}
                >
                  <List.Item.Meta
                    title={
                      <Space>
                        {isSelected && (
                          <CheckCircleOutlined style={{ color: "#25D366" }} />
                        )}
                        <Text strong>{item.name}</Text>
                        <Tag color="blue">{item.language}</Tag>
                        <Tag>{item.category}</Tag>
                      </Space>
                    }
                    description={
                      <div style={{ marginTop: 4 }}>
                        {preview.header?.text && (
                          <div style={{ fontWeight: 500, fontSize: 13 }}>
                            {preview.header.text}
                          </div>
                        )}
                        {preview.body?.text && (
                          <Text
                            type="secondary"
                            style={{ fontSize: 12 }}
                            ellipsis={{ rows: 2 }}
                          >
                            {preview.body.text}
                          </Text>
                        )}
                      </div>
                    }
                  />
                </List.Item>
              );
            }}
          />
        </div>
      )}

      {selectedTemplate && (
        <>
          <Divider style={{ margin: "12px 0" }} />
          <div
            style={{
              background: "#f6f6f6",
              borderRadius: 8,
              padding: 16,
            }}
          >
            <Space style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              <Text strong>Template Preview</Text>
              <Button size="small" type="text" icon={<CopyOutlined />} onClick={handleCopy}>
                Copy
              </Button>
            </Space>
            <div
              style={{
                background: "#DCF8C6",
                borderRadius: 8,
                padding: 12,
                maxWidth: 360,
              }}
            >
              {(() => {
                const preview = getTemplatePreview(selectedTemplate);
                return (
                  <>
                    {preview.header?.text && (
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>
                        {preview.header.text}
                      </div>
                    )}
                    {preview.body?.text && (
                      <pre
                        style={{
                          whiteSpace: "pre-wrap",
                          margin: 0,
                          fontFamily: "inherit",
                          fontSize: 14,
                        }}
                      >
                        {preview.body.text}
                      </pre>
                    )}
                    {preview.footer?.text && (
                      <div style={{ color: "#8696a0", fontSize: 12, marginTop: 6 }}>
                        {preview.footer.text}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </>
      )}
    </Modal>
  );
};

export default SendTemplateModal;
